import dayjs from 'dayjs'
import type { Duration } from 'dayjs/plugin/duration'
import { isNumber } from 'lodash'
import { useCallback } from 'react'

import emDash from '~/components/emdash'

import type { Tuple } from '../types'
import { TimeUnits } from '../types/units'
import { roundNumber, useNumberFormatter } from './number'
import type { FormatterInputScalar } from './types'

export const attributeDurationToDayJsDuration = {
  [TimeUnits.MILLISECONDS]: 'milliseconds',
  [TimeUnits.SECONDS]: 'seconds',
  [TimeUnits.MINUTES]: 'minutes',
  [TimeUnits.HOURS]: 'hours',
  [TimeUnits.DAYS]: 'days'
} as const

export function isTimeUnit(unit?: string): unit is TimeUnits {
  return unit !== undefined && unit in attributeDurationToDayJsDuration
}

export function getDuration(value: number, unit: TimeUnits): Duration
export function getDuration(value: number, unit: string): Duration | null
export function getDuration(value: number, unit: string) {
  if (!isTimeUnit(unit)) return null
  return dayjs.duration(value, attributeDurationToDayJsDuration[unit])
}

type DurationObj = Duration | Partial<{ value: FormatterInputScalar; units: string }> | number | null
type DurationOptions = {
  invalidStr?: string
  compact?: boolean
}

const defaultOptions = {
  invalidStr: emDash,
  compact: false
}

export function useDurationFormatter() {
  const formatNumber = useNumberFormatter()

  return useCallback(
    (durationObj?: DurationObj, options?: DurationOptions) => {
      const { invalidStr, compact } = { ...defaultOptions, ...options }
      const disaggregated = disaggregateDuration(durationObj, { compact })
      if (!disaggregated) return invalidStr
      return disaggregated.map(({ value, units }) => `${formatNumber(value)}${units}`).join(' ')
    },
    [formatNumber]
  )
}

export function disaggregateDuration(durationObj?: DurationObj, options?: Pick<DurationOptions, 'compact'>) {
  const duration = normalizeDurationObj(durationObj)
  if (!duration) return null

  let components: Tuple<{ value: number; units: string }>
  if (duration.asHours() >= 24) {
    const [days, hours] = splitIntoParts(duration.asDays(), duration.asHours(), 24)
    components = [
      { value: days, units: 'd' },
      { value: hours, units: 'h' }
    ]
  } else if (duration.asHours() >= 1) {
    const [hours, minutes] = splitIntoParts(duration.asHours(), duration.asMinutes(), 60)
    components = [
      { value: hours, units: 'h' },
      { value: minutes, units: 'm' }
    ]
  } else {
    const [minutes, seconds] = splitIntoParts(duration.asMinutes(), duration.asSeconds(), 60)
    components = [
      { value: minutes, units: 'm' },
      { value: seconds, units: 's' }
    ]
  }

  if (options?.compact) {
    const [larger, smaller] = components
    if (larger.value === 0) return [smaller]
    if (smaller.value === 0) return [larger]
  }

  return components
}

function splitIntoParts(numLongUnit: number, numShortUnit: number, shortUnitsPerLongUnit: number): Tuple<number> {
  const long = Math.floor(numLongUnit)
  const short = roundNumber(numShortUnit - long * shortUnitsPerLongUnit, 0)
  return short === shortUnitsPerLongUnit ? [long + 1, 0] : [long, short]
}

function normalizeDurationObj(duration?: DurationObj): Duration | undefined {
  if (isNumber(duration)) return dayjs.duration(duration)
  if (dayjs.isDuration(duration)) return duration

  const { value, units } = duration ?? {}
  if (!isNumber(value) || !isTimeUnit(units)) return

  const dayjsDurationUnit = attributeDurationToDayJsDuration[units]
  return dayjs.duration(value, dayjsDurationUnit)
}
